import {
  Alert,
  Button,
  Card,
  DateField,
  Divider,
  Heading,
  NumberField,
  Text,
  TextField,
} from "@/design-system";
import { trackEvent } from "@/lib/dataLayer";
import { Printer } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { BLOG_POSTS, SITE_URL } from "../constants";
import {
  type CarneHeader,
  CarneSlip,
  type CarneSlipData,
} from "../components/carneSlip";
import { RelatedLinks } from "../components/relatedLinks";
import { ToolPageLayout } from "../components/toolPageLayout";
import { ToolResultCta } from "../components/toolResultCta";
import {
  addMonths,
  formatCurrency,
  formatDate,
  pricePayment,
} from "../utils/finance";
import {
  DESCRIPTION,
  FAQ_ITEMS,
  MAX_INSTALLMENTS,
  PATH,
  TITLE,
  TRAIL,
  WEB_APPLICATION,
} from "./constants";

export const GeradorDeCarne = () => {
  const [header, setHeader] = useState<CarneHeader>({
    storeName: "",
    storeContact: "",
    pixKey: "",
    customerName: "",
    customerDocument: "",
    orderReference: "",
  });
  const [price, setPrice] = useState<number | undefined>(1200);
  const [downPayment, setDownPayment] = useState<number | undefined>(0);
  const [installments, setInstallments] = useState<number | undefined>(10);
  const [ratePercent, setRatePercent] = useState<number | undefined>(0);
  const [firstDueDate, setFirstDueDate] = useState<Date | null>(() =>
    addMonths(new Date(), 1)
  );
  const [showErrors, setShowErrors] = useState(false);
  const storeNameRef = useRef<HTMLInputElement>(null);
  const customerNameRef = useRef<HTMLInputElement>(null);

  const setField =
    (field: keyof CarneHeader) => (event: { target: { value: string } }) =>
      setHeader(current => ({ ...current, [field]: event.target.value }));

  const financed = (price ?? 0) - (downPayment ?? 0);
  const downPaymentError =
    (price ?? 0) > 0 && financed <= 0
      ? "A entrada precisa ser menor que o valor da compra."
      : undefined;
  const installmentsError =
    !installments || installments < 1
      ? `Informe de 1 a ${MAX_INSTALLMENTS} parcelas.`
      : undefined;
  const storeNameError =
    showErrors && !header.storeName.trim()
      ? "Informe o nome da loja que aparece no carnê."
      : undefined;
  const customerNameError =
    showErrors && !header.customerName.trim()
      ? "Informe o nome do cliente."
      : undefined;

  const slips = useMemo<CarneSlipData[]>(() => {
    if (financed <= 0 || !installments || installmentsError || !firstDueDate) {
      return [];
    }
    const rate = (ratePercent ?? 0) / 100;
    // Sem juros, os centavos que sobram da divisão vão para a última parcela —
    // a soma do carnê tem de bater com o valor da venda.
    const base =
      rate > 0
        ? Math.round(pricePayment(financed, rate, installments) * 100) / 100
        : Math.floor((financed / installments) * 100) / 100;
    return Array.from({ length: installments }, (_, index) => ({
      number: index + 1,
      total: installments,
      dueDate: addMonths(firstDueDate, index),
      amount:
        rate === 0 && index === installments - 1
          ? Math.round((financed - base * (installments - 1)) * 100) / 100
          : base,
    }));
  }, [financed, installments, installmentsError, ratePercent, firstDueDate]);

  const print = () => {
    if (!header.storeName.trim()) {
      setShowErrors(true);
      storeNameRef.current?.focus();
      return;
    }
    if (!header.customerName.trim()) {
      setShowErrors(true);
      customerNameRef.current?.focus();
      return;
    }
    trackEvent({
      event: "tool_action",
      tool: "gerador_carne",
      action: "print",
    });
    window.print();
  };

  return (
    <ToolPageLayout
      title={TITLE}
      description={DESCRIPTION}
      trail={TRAIL}
      schemas={[WEB_APPLICATION]}
      eyebrow="Ferramenta grátis · sem cadastro"
      heading="Gerador de carnê de crediário grátis"
      lead="Preencha os dados da venda e o carnê aparece pronto ao lado, com canhoto para a loja e via para o cliente. Imprima ou salve em PDF na hora."
      faqItems={FAQ_ITEMS}
      faqHeading="Dúvidas sobre o carnê de crediário"
    >
      {openDemo => (
        <>
          <section
            aria-labelledby="carne-form-title"
            className="mx-auto grid w-full max-w-6xl grid-cols-1 items-start gap-6 px-4 sm:px-6 lg:grid-cols-5 lg:px-8 print:block print:max-w-none print:p-0"
          >
            {/* `ph-no-capture` aqui e no carnê: nome e CPF do cliente nunca
                entram em gravação de sessão nem em autocapture do PostHog. */}
            <Card className="ph-no-capture flex min-w-0 flex-col gap-5 lg:col-span-2 print:hidden">
              <Heading level={2} size={4} id="carne-form-title">
                Dados do carnê
              </Heading>

              <Heading level={3} size={4} className="text-ds-body">
                Loja
              </Heading>
              <TextField
                ref={storeNameRef}
                label="Nome da loja"
                autoComplete="organization"
                value={header.storeName}
                onChange={setField("storeName")}
                error={storeNameError}
                required
              />
              <TextField
                label="Telefone ou CNPJ da loja"
                value={header.storeContact}
                onChange={setField("storeContact")}
              />
              <TextField
                label="Chave Pix para pagamento"
                hint="Aparece em cada parcela para o cliente pagar sem ir à loja."
                value={header.pixKey}
                onChange={setField("pixKey")}
              />

              <Divider />
              <Heading level={3} size={4} className="text-ds-body">
                Cliente
              </Heading>
              <TextField
                ref={customerNameRef}
                label="Nome do cliente"
                autoComplete="off"
                value={header.customerName}
                onChange={setField("customerName")}
                error={customerNameError}
                required
              />
              <TextField
                label="CPF do cliente"
                inputMode="numeric"
                autoComplete="off"
                value={header.customerDocument}
                onChange={setField("customerDocument")}
              />
              <TextField
                label="Referência da venda"
                hint="Número do pedido ou o produto vendido."
                value={header.orderReference}
                onChange={setField("orderReference")}
              />

              <Divider />
              <Heading level={3} size={4} className="text-ds-body">
                Parcelamento
              </Heading>
              <NumberField
                label="Valor da compra"
                required
                prefix="R$"
                decimals={2}
                max={10_000_000}
                value={price}
                onValueChange={setPrice}
              />
              <NumberField
                label="Entrada"
                hint="Deixe zerado se a venda não tem entrada."
                prefix="R$"
                decimals={2}
                max={10_000_000}
                value={downPayment}
                onValueChange={setDownPayment}
                error={downPaymentError}
              />
              <NumberField
                label="Número de parcelas"
                required
                decimals={0}
                max={MAX_INSTALLMENTS}
                value={installments}
                onValueChange={setInstallments}
                error={installmentsError}
              />
              <NumberField
                label="Juros ao mês"
                hint="Com juros, as parcelas seguem a Tabela Price."
                suffix="%"
                decimals={2}
                max={30}
                value={ratePercent}
                onValueChange={setRatePercent}
              />
              <DateField
                label="Primeiro vencimento"
                value={firstDueDate}
                onValueChange={setFirstDueDate}
                required
              />

              <Alert tone="info">
                Os dados do carnê ficam só no seu navegador e não são guardados
                pela Fidexa.
              </Alert>
            </Card>

            <div className="flex min-w-0 flex-col gap-4 lg:col-span-3 print:gap-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
                <div className="flex flex-col gap-1" aria-live="polite">
                  <Heading level={2} size={4}>
                    Pré-visualização
                  </Heading>
                  {slips.length > 0 ? (
                    <Text size="small" tone="subtle">
                      {slips.length} parcelas · 1º vencimento em{" "}
                      {formatDate(slips[0].dueDate)}
                      {(downPayment ?? 0) > 0
                        ? ` · entrada de ${formatCurrency(downPayment ?? 0)}`
                        : ""}
                    </Text>
                  ) : null}
                </div>
                <Button
                  size="lg"
                  iconLeft={<Printer aria-hidden="true" className="size-5" />}
                  onClick={print}
                  disabled={slips.length === 0}
                  className="w-full sm:w-auto"
                >
                  Imprimir ou salvar em PDF
                </Button>
              </div>

              {slips.length > 0 ? (
                <ol
                  aria-label="Folhas do carnê"
                  className="ph-no-capture flex flex-col gap-3 print:gap-2"
                >
                  {slips.map(slip => (
                    <CarneSlip key={slip.number} slip={slip} header={header} />
                  ))}
                </ol>
              ) : (
                <Alert tone="info" title="Complete o parcelamento">
                  Informe o valor da compra, o número de parcelas e o primeiro
                  vencimento para montar o carnê.
                </Alert>
              )}

              <Text size="caption" tone="subtle" className="hidden print:block">
                Carnê gerado grátis em {SITE_URL.replace("https://", "")}
                {PATH}
              </Text>
            </div>
          </section>

          <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 print:hidden">
            <ToolResultCta
              id="carne-cta-title"
              title="Carnê impresso não lembra o cliente do vencimento"
              description="A Fidexa avisa cada parcela pelo WhatsApp antes de vencer, recebe por Pix e cobra o atraso sozinha."
              onDemo={openDemo}
            />
          </div>

          <RelatedLinks
            current="gerador_carne"
            posts={[BLOG_POSTS.reguaAutomatica, BLOG_POSTS.negativar]}
          />
        </>
      )}
    </ToolPageLayout>
  );
};
